import { useCallback, useMemo } from 'react';
import { getPath } from "../utils/path.js";
import { getShapeTitle } from "../utils/refs.js";
import { getDiscriminatorOptions, getDiscriminatorProperty, resolveRef } from "../utils/schema-context.js";
import { useFormStore, useFormStoreSelector, useForms } from "./use-form-store.js";
import { useSchemaContext } from "./use-schema-context.js";
export function useDiscriminatorField({ path, schema, discriminatorPath }) {
    const store = useFormStore();
    const ctx = useSchemaContext();
    const forms = useForms();
    const resolvedSchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    const discriminatorValuePath = path ? `${path}.${discriminatorPath}` : discriminatorPath;
    const value = useFormStoreSelector((s) => getPath(s.values, discriminatorValuePath));
    const options = useMemo(() => {
        const rawOptions = getDiscriminatorOptions(ctx, resolvedSchema, discriminatorPath);
        // Look up form config titles for each variant
        return rawOptions.map((opt) => {
            const formTitle = getShapeTitle(forms, opt.ref);
            if (formTitle) {
                return { ...opt, label: formTitle };
            }
            return opt;
        });
    }, [ctx, resolvedSchema, discriminatorPath, forms]);
    const currentOption = useMemo(() => (value ? options.find((opt) => opt.value === value) : undefined), [options, value]);
    const currentSchema = currentOption?.schema;
    const currentRef = currentOption?.ref;
    const onChange = useCallback((newValue) => store.getState().setDiscriminatorValue(path, discriminatorPath, newValue), [store, path, discriminatorPath]);
    return { value, options, currentSchema, currentRef, onChange };
}
export function useAutoDiscriminatorField({ path, schema }) {
    const ctx = useSchemaContext();
    const resolvedSchema = useMemo(() => resolveRef(ctx, schema), [ctx, schema]);
    const discriminatorPath = useMemo(() => getDiscriminatorProperty(ctx, resolvedSchema), [ctx, resolvedSchema]);
    const result = useDiscriminatorField({ path, schema: resolvedSchema, discriminatorPath: discriminatorPath ?? '' });
    return { ...result, discriminatorPath };
}
